/**
 * /app/$tenantSlug/accounting/reports/tds-receivable — TDS receivable /
 * Form 26AS reconciliation (lite books).
 *
 * Customers deduct TDS (IT Act §194C / §194J / …) from what they pay and
 * report it against our PAN; it shows in Form 26AS / AIS by deductor TAN
 * and section. Receipts record that TDS per invoice (Dr 1400 TDS
 * Receivable). This page lists every such deduction on a posted receipt in
 * the range, grouped 26AS-style (quarter × customer TAN × section), with
 * the invoice + receipt behind each, and sets the 1400 ledger beside it
 * so a difference (manual journals, opening balances, voided receipts)
 * shows up. A reconciliation aid — not a filing.
 *
 * Endpoints: GET /api/v1/accounting/reports/tds-receivable[.csv]?from=&to=
 * (default: the current Indian FY). Linked from the Reports hub. Gates come
 * from AccountingModuleShell (feature "accounting" + accountingRole).
 */

import { useState } from "react";
import { createFileRoute, Link, useParams } from "@tanstack/react-router";
import { AlertTriangle, ArrowLeft, Download, Info, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { accountingApi, toApiError } from "@/lib/api";
import { fmtDate, formatPaise, todayISO } from "@/lib/expense-format";
import {
  fyLabel,
  fyQuarterRange,
  fyRange,
  fyStartYear,
  type FYQuarter,
} from "@/lib/indian-fy";
import { useTDSReceivableReport } from "@/hooks/use-accounting";
import type { TDSReceivableResult } from "@/types/accounting";
import { AccountingModuleShell } from "@/components/shared/accounting-module-shell";
import { EmptyState } from "@/components/shared/empty-state";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";
import { useSmPlus } from "@/lib/use-sm-plus";

export const Route = createFileRoute(
  "/_authgated/app/$tenantSlug/accounting/reports/tds-receivable",
)({
  component: TDSReceivableRoute,
});

const QUARTERS: readonly FYQuarter[] = [1, 2, 3, 4];

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function TDSReceivableRoute() {
  const { tenantSlug } = useParams({ strict: false }) as { tenantSlug: string };
  const currentFY = fyStartYear(todayISO());
  const initial = fyRange(currentFY);
  const [fy, setFY] = useState<number>(currentFY);
  const [range, setRange] = useState(initial);
  const [pendFrom, setPendFrom] = useState(initial.from);
  const [pendTo, setPendTo] = useState(initial.to);
  const [downloading, setDownloading] = useState(false);

  const query = useTDSReceivableReport(range.from, range.to);
  const data = query.data ?? null;
  const error = query.isError ? toApiError(query.error).message : null;

  function apply(next: { from: string; to: string }) {
    setRange(next);
    setPendFrom(next.from);
    setPendTo(next.to);
  }

  function pickFY(startYear: number) {
    setFY(startYear);
    apply(fyRange(startYear));
  }

  async function onDownloadCSV() {
    setDownloading(true);
    try {
      const blob = await accountingApi.downloadTDSReceivableCSV(range);
      downloadBlob(blob, `tds-receivable-${range.from}-to-${range.to}.csv`);
    } catch (err) {
      toast.error(`Could not download the CSV: ${toApiError(err).message}`);
    } finally {
      setDownloading(false);
    }
  }

  const isRange = (r: { from: string; to: string }) =>
    r.from === range.from && r.to === range.to;

  return (
    <AccountingModuleShell
      title="TDS Receivable"
      description="TDS your customers deducted, to tick against Form 26AS / AIS."
      actions={
        <div className="flex flex-wrap items-center gap-2">
          <Button asChild variant="outline" className="gap-1.5">
            <Link to="/app/$tenantSlug/accounting/reports" params={{ tenantSlug }}>
              <ArrowLeft aria-hidden className="h-4 w-4" />
              Back
            </Link>
          </Button>
          <Button
            className="gap-1.5"
            onClick={() => void onDownloadCSV()}
            disabled={downloading || range.to < range.from}
            data-testid="tds-receivable-csv"
          >
            {downloading ? (
              <Loader2 aria-hidden className="h-4 w-4 animate-spin" />
            ) : (
              <Download aria-hidden className="h-4 w-4" />
            )}
            Download CSV
          </Button>
        </div>
      }
    >
      <Card>
        <CardContent className="space-y-4 py-4">
          <div className="flex flex-wrap items-center gap-2">
            {[currentFY, currentFY - 1].map((y) => (
              <Button
                key={y}
                type="button"
                size="sm"
                variant={isRange(fyRange(y)) ? "default" : "outline"}
                onClick={() => pickFY(y)}
              >
                {y === currentFY ? "This FY" : "Last FY"} ({fyLabel(y).slice(3)})
              </Button>
            ))}
            <span className="mx-1 hidden h-5 w-px bg-border sm:inline-block" />
            {QUARTERS.map((q) => {
              const r = fyQuarterRange(fy, q);
              return (
                <Button
                  key={q}
                  type="button"
                  size="sm"
                  variant={isRange(r) ? "default" : "outline"}
                  onClick={() => apply(r)}
                  title={`${fmtDate(r.from)} – ${fmtDate(r.to)}`}
                >
                  Q{q}
                </Button>
              );
            })}
            <span className="text-xs text-muted-foreground">of {fyLabel(fy)}</span>
          </div>
          <div className="grid gap-3 sm:grid-cols-[1fr_1fr_auto]">
            <div className="grid gap-1.5">
              <Label htmlFor="tds-from">From</Label>
              <Input
                id="tds-from"
                type="date"
                value={pendFrom}
                onChange={(e) => setPendFrom(e.target.value)}
              />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="tds-to">To</Label>
              <Input
                id="tds-to"
                type="date"
                value={pendTo}
                onChange={(e) => setPendTo(e.target.value)}
              />
            </div>
            <div className="flex items-end">
              <Button
                type="button"
                onClick={() => {
                  if (pendFrom) setFY(fyStartYear(pendFrom));
                  apply({ from: pendFrom, to: pendTo });
                }}
                disabled={
                  !pendFrom ||
                  !pendTo ||
                  pendTo < pendFrom ||
                  (pendFrom === range.from && pendTo === range.to)
                }
              >
                Refresh
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Alert className="mt-4">
        <Info aria-hidden className="h-4 w-4" />
        <AlertDescription>
          Tick these against Form 26AS / AIS on the income-tax portal
          (TRACES). A reconciliation aid — not a filing.
        </AlertDescription>
      </Alert>

      {error && (
        <Alert variant="destructive" className="mt-4">
          <AlertTitle>Could not load the TDS receivable report</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {query.isLoading ? (
        <div className="mt-4 space-y-2">
          <div className="grid gap-4 sm:grid-cols-3">
            {["a", "b", "c"].map((k) => (
              <Skeleton key={k} className="h-24" />
            ))}
          </div>
          {["d", "e", "f"].map((k) => (
            <Skeleton key={k} className="h-10" />
          ))}
        </div>
      ) : data ? (
        <TDSReceivableBody data={data} tenantSlug={tenantSlug} />
      ) : null}
    </AccountingModuleShell>
  );
}

function SummaryCard({
  label,
  value,
  sub,
  warn = false,
}: Readonly<{ label: string; value: string; sub?: string; warn?: boolean }>) {
  return (
    <Card className={cn(warn && "border-amber-300")}>
      <CardContent className="space-y-1 py-4">
        <p className="text-sm text-muted-foreground">{label}</p>
        <p className="text-2xl font-semibold tabular-nums">{value}</p>
        {sub && <p className="text-xs text-muted-foreground">{sub}</p>}
      </CardContent>
    </Card>
  );
}

function TDSReceivableBody({
  data,
  tenantSlug,
}: Readonly<{ data: TDSReceivableResult; tenantSlug: string }>) {
  const [showRows, setShowRows] = useState(false);
  const isSmPlus = useSmPlus();
  const ledger = data.ledger;
  const linkClass = "font-medium text-primary underline-offset-4 hover:underline";

  return (
    <div className="mt-4 space-y-4">
      <p className="text-xs text-muted-foreground">
        {fmtDate(data.from)} – {fmtDate(data.to)}
      </p>

      <div className="grid gap-4 sm:grid-cols-3">
        <SummaryCard
          label="TDS Deducted by Customers"
          value={formatPaise(data.total_tds_minor)}
          sub={`On ${formatPaise(data.total_gross_minor)} settled (cash + TDS)`}
        />
        <SummaryCard
          label={`${ledger.account_code || "1400"} TDS Receivable — Closing Balance`}
          value={ledger.account_found ? formatPaise(ledger.closing_minor) : "—"}
          sub={
            ledger.account_found
              ? `Opening ${formatPaise(ledger.opening_minor)} · debits ${formatPaise(ledger.debits_minor)} · credits ${formatPaise(ledger.credits_minor)}`
              : `Account ${ledger.account_code || "1400"} isn't in your chart of accounts.`
          }
        />
        <SummaryCard
          label="Unexplained Difference"
          value={formatPaise(ledger.unexplained_minor)}
          warn={ledger.unexplained_minor !== 0}
          sub={
            ledger.unexplained_minor === 0
              ? "The ledger matches the receipts below."
              : "TDS booked by manual journal or opening balance, or a receipt posted and voided in this period."
          }
        />
      </div>

      {data.missing_tan.length > 0 && (
        <Alert variant="destructive" data-testid="tds-receivable-missing-tan">
          <AlertTriangle aria-hidden className="h-4 w-4" />
          <AlertTitle>Missing TAN</AlertTitle>
          <AlertDescription>
            {data.missing_tan.join(", ")}. 26AS lists deductors by TAN — add it
            under{" "}
            <Link
              to="/app/$tenantSlug/accounting/contacts"
              params={{ tenantSlug }}
              className="font-medium underline underline-offset-4"
            >
              Accounting → Contacts
            </Link>
            .
          </AlertDescription>
        </Alert>
      )}

      {data.deductors.length === 0 ? (
        <EmptyState
          title="No Customer TDS in This Period"
          description="Receipts where a customer deducted TDS show up here once they are posted."
        />
      ) : (
        <>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">By Deductor (As in 26AS)</CardTitle>
            </CardHeader>
            <CardContent>
              {!isSmPlus ? (
                // Phones: one row per deductor — customer and TDS on top,
                // quarter · TAN · section · entries and gross underneath.
                <ul className="divide-y rounded-md border" data-testid="tds-deductor-cards">
                  {data.deductors.map((d) => (
                    <li
                      key={`${d.quarter}-${d.contact_id}-${d.section}`}
                      className="space-y-1 px-3 py-2.5 text-sm"
                    >
                      <div className="flex items-baseline justify-between gap-3">
                        <span className="min-w-0 break-words font-medium text-gray-900">
                          {d.contact_name}
                        </span>
                        <span className="shrink-0 whitespace-nowrap font-semibold tabular-nums">
                          {formatPaise(d.tds_minor)}
                        </span>
                      </div>
                      <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-muted-foreground">
                        <span className="whitespace-nowrap">{d.quarter}</span>
                        {d.contact_tan ? (
                          <span className="font-mono">{d.contact_tan}</span>
                        ) : (
                          <span className="text-destructive">TAN missing</span>
                        )}
                        {d.section && <span>§{d.section}</span>}
                        <span className="whitespace-nowrap">
                          {d.entries} {d.entries === 1 ? "entry" : "entries"}
                        </span>
                        <span className="whitespace-nowrap tabular-nums">
                          Gross {formatPaise(d.gross_minor)}
                        </span>
                      </div>
                    </li>
                  ))}
                  <li className="grid grid-cols-2 gap-3 bg-muted/40 px-3 py-2.5 text-sm">
                    <div>
                      <p className="text-xs text-muted-foreground">Total Gross</p>
                      <p className="font-semibold tabular-nums">
                        {formatPaise(data.total_gross_minor)}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-muted-foreground">Total TDS</p>
                      <p className="font-semibold tabular-nums">
                        {formatPaise(data.total_tds_minor)}
                      </p>
                    </div>
                  </li>
                </ul>
              ) : (
              <div className="overflow-x-auto rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Quarter</TableHead>
                      <TableHead>Customer</TableHead>
                      <TableHead>TAN</TableHead>
                      <TableHead>Section</TableHead>
                      <TableHead className="text-right">Entries</TableHead>
                      <TableHead className="text-right">Gross</TableHead>
                      <TableHead className="text-right">TDS</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.deductors.map((d) => (
                      <TableRow key={`${d.quarter}-${d.contact_id}-${d.section}`}>
                        <TableCell className="whitespace-nowrap">{d.quarter}</TableCell>
                        <TableCell>{d.contact_name}</TableCell>
                        <TableCell className="font-mono text-xs">
                          {d.contact_tan || (
                            <span className="font-sans text-destructive">Missing</span>
                          )}
                        </TableCell>
                        <TableCell>{d.section || "—"}</TableCell>
                        <TableCell className="text-right tabular-nums">{d.entries}</TableCell>
                        <TableCell className="text-right tabular-nums">
                          {formatPaise(d.gross_minor)}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {formatPaise(d.tds_minor)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                  <TableFooter>
                    <TableRow>
                      <TableCell colSpan={5} className="font-medium">
                        Total
                      </TableCell>
                      <TableCell className="text-right font-semibold tabular-nums">
                        {formatPaise(data.total_gross_minor)}
                      </TableCell>
                      <TableCell className="text-right font-semibold tabular-nums">
                        {formatPaise(data.total_tds_minor)}
                      </TableCell>
                    </TableRow>
                  </TableFooter>
                </Table>
              </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex-row items-center justify-between gap-2 space-y-0 pb-2">
              <CardTitle className="text-base">
                Each Deduction ({data.rows.length})
              </CardTitle>
              <Button
                type="button"
                size="sm"
                variant="outline"
                aria-expanded={showRows}
                onClick={() => setShowRows((v) => !v)}
                data-testid="tds-receivable-toggle-rows"
              >
                {showRows ? "Hide" : "Show"}
              </Button>
            </CardHeader>
            {showRows && (
              <CardContent>
                {!isSmPlus ? (
                  // Phones: customer and TDS on top; invoice · receipt ·
                  // quarter · section, then cash and gross underneath.
                  <ul className="divide-y rounded-md border" data-testid="tds-row-cards">
                    {data.rows.map((r, i) => (
                      <li
                        key={`${r.receipt_id}-${r.invoice_id}-${i}`}
                        className="space-y-1 px-3 py-2.5 text-sm"
                      >
                        <div className="flex items-baseline justify-between gap-3">
                          <span className="min-w-0 break-words font-medium text-gray-900">
                            {r.contact_name}
                          </span>
                          <span className="shrink-0 whitespace-nowrap font-semibold tabular-nums">
                            {formatPaise(r.tds_minor)}
                          </span>
                        </div>
                        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
                          <Link
                            to="/app/$tenantSlug/accounting/sales/invoices/$id"
                            params={{ tenantSlug, id: String(r.invoice_id) }}
                            className={cn(linkClass, "inline-flex min-h-6 items-center")}
                          >
                            {r.invoice_no || `#${r.invoice_id}`}
                          </Link>
                          <Link
                            to="/app/$tenantSlug/accounting/money/receipts/$id"
                            params={{ tenantSlug, id: String(r.receipt_id) }}
                            className={cn(linkClass, "inline-flex min-h-6 items-center")}
                          >
                            {r.receipt_no || `#${r.receipt_id}`}
                          </Link>
                          <span className="whitespace-nowrap">{fmtDate(r.receipt_date)}</span>
                          <span className="whitespace-nowrap">{r.quarter}</span>
                          {r.section && <span>§{r.section}</span>}
                        </div>
                        <div className="flex flex-wrap gap-x-3 text-xs tabular-nums text-muted-foreground">
                          <span className="whitespace-nowrap">Cash {formatPaise(r.cash_minor)}</span>
                          <span className="whitespace-nowrap">Gross {formatPaise(r.gross_minor)}</span>
                        </div>
                      </li>
                    ))}
                  </ul>
                ) : (
                <div className="overflow-x-auto rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Quarter</TableHead>
                        <TableHead>Customer</TableHead>
                        <TableHead>Invoice</TableHead>
                        <TableHead>Receipt</TableHead>
                        <TableHead>Section</TableHead>
                        <TableHead className="text-right">Cash</TableHead>
                        <TableHead className="text-right">TDS</TableHead>
                        <TableHead className="text-right">Gross</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {data.rows.map((r, i) => (
                        <TableRow key={`${r.receipt_id}-${r.invoice_id}-${i}`}>
                          <TableCell className="whitespace-nowrap">{r.quarter}</TableCell>
                          <TableCell>
                            {r.contact_name}
                            {r.contact_tan && (
                              <span className="block font-mono text-xs text-muted-foreground">
                                {r.contact_tan}
                              </span>
                            )}
                          </TableCell>
                          <TableCell className="whitespace-nowrap">
                            <Link
                              to="/app/$tenantSlug/accounting/sales/invoices/$id"
                              params={{ tenantSlug, id: String(r.invoice_id) }}
                              className={linkClass}
                            >
                              {r.invoice_no || `#${r.invoice_id}`}
                            </Link>
                            <span className="block text-xs text-muted-foreground">
                              {fmtDate(r.invoice_date)}
                            </span>
                          </TableCell>
                          <TableCell className="whitespace-nowrap">
                            <Link
                              to="/app/$tenantSlug/accounting/money/receipts/$id"
                              params={{ tenantSlug, id: String(r.receipt_id) }}
                              className={linkClass}
                            >
                              {r.receipt_no || `#${r.receipt_id}`}
                            </Link>
                            <span className="block text-xs text-muted-foreground">
                              {fmtDate(r.receipt_date)}
                            </span>
                          </TableCell>
                          <TableCell>{r.section || "—"}</TableCell>
                          <TableCell className="text-right tabular-nums">
                            {formatPaise(r.cash_minor)}
                          </TableCell>
                          <TableCell className="text-right tabular-nums">
                            {formatPaise(r.tds_minor)}
                          </TableCell>
                          <TableCell className="text-right tabular-nums">
                            {formatPaise(r.gross_minor)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
                )}
              </CardContent>
            )}
          </Card>
        </>
      )}
    </div>
  );
}
